import { FC, ReactNode } from 'react';
import moment from 'moment';
import { Loader, Typography } from 'team-hero-ui';
import { useTranslation } from 'react-i18next';

import { useGetPublicSystemSettingsQuery } from 'services/publicTeamHeroApi.service';
import { LinkHrefStyled } from 'helpers/link/link.helper';
import {
  LoginContentStyled,
  LoginContentWrapperStyled,
  LoginCopyrightLinksStyled,
  LoginCopyrightStyled,
  LoginCopyrightWrapperStyled,
  LoginWrapperCardStyled,
  LoginWrapperStyled,
  LogoStyled,
  LogoWrapperStyled,
} from './LayoutLogin.styled';

interface ILayoutLoginProps {
  children: ReactNode;
}

const LayoutLogin: FC<ILayoutLoginProps> = ({ children }) => {
  const { t } = useTranslation(['general', 'errors']);

  const { data: systemSettingsData, isLoading: isLoadingSystemSettings } =
    useGetPublicSystemSettingsQuery();

  const logoUrlPath = systemSettingsData?.settings.branding?.client_logo?.file;
  const startGradientColor =
    systemSettingsData?.settings.branding?.background_gradient_color1;
  const endGradientColor =
    systemSettingsData?.settings.branding?.background_gradient_color2;
  const imprintUrl = systemSettingsData?.settings.copyright.link;
  const privacyPolicyUrl = systemSettingsData?.settings.privacy_policy_url;

  const getLogoUrlBasePath = (): string => {
    if (
      import.meta.env.MODE === 'development' ||
      import.meta.env.MODE === 'mobile'
    ) {
      return import.meta.env.VITE_API_URI || '';
    }
    return '';
  };

  if (isLoadingSystemSettings) {
    return <Loader />;
  }

  return (
    <LoginWrapperStyled
      $startGradientColor={startGradientColor}
      $endGradientColor={endGradientColor}
    >
      <LoginContentStyled>
        <LogoWrapperStyled>
          {logoUrlPath && (
            <LogoStyled
              src={getLogoUrlBasePath() + logoUrlPath}
              alt={
                systemSettingsData?.settings.branding?.client_logo?.alt ||
                'logo'
              }
            />
          )}
        </LogoWrapperStyled>
        <LoginContentWrapperStyled>
          <LoginWrapperCardStyled>{children}</LoginWrapperCardStyled>
        </LoginContentWrapperStyled>
        <LoginCopyrightWrapperStyled>
          <LoginCopyrightStyled>
            <Typography.Copy weight='semiBold' size='S'>
              {t('general:copyright', { year: moment().year() })}{' '}
              <LinkHrefStyled
                href={systemSettingsData?.settings.copyright.link}
                rel='noopener noreferrer'
                target='_blank'
              >
                {systemSettingsData?.settings.copyright.company}
              </LinkHrefStyled>
            </Typography.Copy>
            {(imprintUrl || privacyPolicyUrl) && (
              <LoginCopyrightLinksStyled>
                {imprintUrl && (
                  <Typography.Copy weight='semiBold' size='S'>
                    <LinkHrefStyled
                      href={imprintUrl}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {t('general:login.imprint')}
                    </LinkHrefStyled>
                  </Typography.Copy>
                )}
                {imprintUrl && privacyPolicyUrl && (
                  <Typography.Copy weight='semiBold' size='S'>
                    |
                  </Typography.Copy>
                )}
                {privacyPolicyUrl && (
                  <Typography.Copy weight='semiBold' size='S'>
                    <LinkHrefStyled
                      href={privacyPolicyUrl}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {t('general:login.dataPrivacy')}
                    </LinkHrefStyled>
                  </Typography.Copy>
                )}
              </LoginCopyrightLinksStyled>
            )}
          </LoginCopyrightStyled>
        </LoginCopyrightWrapperStyled>
      </LoginContentStyled>
    </LoginWrapperStyled>
  );
};

export default LayoutLogin;
